/**
 * Booking Mutation Hooks
 * Mutations for booking meetings and managing bookings
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../useAuth";
import { apiPost } from "../../../lib/api-utils";
import { useToast } from "../../use-toast";
import { ENDPOINTS, TOAST_MESSAGES, RETRY_CONFIG } from "../config";
import { queryKeys, getCalendarQueryKeysToInvalidate } from "../query-keys";
import type { BookingData } from "../types";
import { getErrorMessage } from "../utils";

/**
 * Book Meeting Slot
 * Creates a new booking for a meeting
 */
export const useBookingMutation = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (bookingData: BookingData) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiPost(
        ENDPOINTS.booking.externalEventBooking,
        bookingData,
        token
      );

      return response;
    },
    onSuccess: () => {
      toast(TOAST_MESSAGES.booking.success);

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.booking.openSlots(),
      });

      // Invalidate calendar queries since new event was created
      getCalendarQueryKeysToInvalidate().forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.booking.error.description
      );
      toast({
        title: TOAST_MESSAGES.booking.error.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};
